import React, { useEffect, useState } from "react";
import { captureMessage } from "@sentry/browser";
import { RouteComponentProps, useHistory, useLocation } from "react-router-dom";
import { buildLink } from "common/routing";
import { toSentenceCase } from "common/utils/strings";
import {
  AiActionRunStatus,
  useGetAiActionRunQuery,
} from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import HeaderNav from "../Common/HeaderNav";
import Illustration from "../Common/Illustration";
import { Body } from "../Common/Typography";
import {
  Container,
  Content,
  Page,
  Shimmer,
  TextBox,
} from "./__styles__/AIActionGeneratingDocument";
export type AiActionGeneratingDocumentProps = Pick<
  RouteComponentProps<{ id: string }>,
  "match"
>;

export const cyclingTexts = [
  "Setting up your file framework…",
  "Pulling in key details from your responses…",
  "Drafting the sections based on your selections…",
  "Organizing information for clarity and compliance…",
  "Pre-filling standard language to save you time…",
  "Customizing content to match your needs…",
  "Reviewing requirements to ensure completeness…",
  "Filling in supporting details…",
  "Adding final touches to your file…",
  "Finalizing formatting and preparing your file…",
];

export default ({ match }: AiActionGeneratingDocumentProps) => {
  const { id } = match.params;
  const { addSuccessToast, addErrorToast } = useStatusToasts();
  const location = useLocation<{ prevLocation?: string }>();
  const prevLocation = location.state?.prevLocation;
  const history = useHistory();
  const [aiActionName, setAiActionName] = useState<string | null>(null);

  const [cyclingTextIndex, setCyclingTextIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCyclingTextIndex(prevIndex =>
        prevIndex === cyclingTexts.length - 1 ? prevIndex : prevIndex + 1,
      );
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const { stopPolling } = useGetAiActionRunQuery({
    variables: { id },
    pollInterval: 2000,
    onCompleted: data => {
      const { results, status } = data.aiActionRun;
      setAiActionName(data.aiActionRun.aiAction.name);
      if (status === AiActionRunStatus.FAILED) {
        addErrorToast(
          "There was an issue running the AI action. Please try again. If the problem persists, please email us at support@withforerunner.com",
        );
        stopPolling();

        return;
      }

      if (status !== AiActionRunStatus.SUCCEEDED) {
        return;
      }

      if (
        results[0]?.__typename ===
        "SubmissionAIActionDocumentGenerationRunResult"
      ) {
        addSuccessToast("AI action ran successfully.");
        history.push(
          buildLink("documentUploadDetail", {
            id: results[0].document.id,
          }),
        );
      } else {
        captureMessage("Running AI action returned unexpected result");
        addErrorToast(
          "There was an issue running the AI action. Please try again. If the problem persists, please email us at support@withforerunner.com",
        );
      }

      stopPolling();
    },
    onError: () => {
      stopPolling();
      addErrorToast(
        "There was an issue running the AI action. Please try again. If the problem persists, please email us at support@withforerunner.com",
      );
    },
  });

  return (
    <Page>
      <HeaderNav
        subtitle={aiActionName ?? "Generating file"}
        prevLocation={prevLocation}
      />
      <Content>
        <Container aria-label="generating-file">
          <Illustration alt={toSentenceCase("add-file")} name={"add-file"} />
          <TextBox>
            <Body size="large" type="emphasis" color="contentPrimary" as="span">
              <Shimmer>Generating file</Shimmer>
            </Body>
            <Body size="large" type="regular" color="contentSecondary">
              {cyclingTexts[cyclingTextIndex]}
            </Body>
          </TextBox>
        </Container>
      </Content>
    </Page>
  );
};
